// ═══════════════════════════════════════════════════════════════════════════════
//  APP THEME SYSTEM — single source of truth for all visual design
//
//  Usage in any component:
//    import { getTheme, ALL_THEMES, FONT } from "../theme/appTheme";
//
//  Theme shape — all keys are used by page components via useTheme():
//    bg, panel, border, text, muted, bright, eqBg, eqBorder, cardBg  ← legacy keys
//    accent, accentSoft, dark, name, id, font, monoFont               ← new keys
//
//  To add a new theme: add an entry to THEMES below, all pages pick it up automatically.
// ═══════════════════════════════════════════════════════════════════════════════

// ── Typography ────────────────────────────────────────────────────────────────
// Loaded via public/index.html Google Fonts link
export const FONT = {
  sans:    "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  mono:    "'JetBrains Mono', 'Fira Code', Consolas, 'Courier New', monospace",
  heading: "'Sora', 'Inter', -apple-system, sans-serif",
};

// ── Theme definitions ─────────────────────────────────────────────────────────
// All themes must define the same set of tokens.
// page files never import theme tokens directly — they only use useTheme().

const base = {
  font:     FONT.sans,
  monoFont: FONT.mono,
};

export const THEMES = {

  // ── 1. Deep Space (dark, space-navy — refined default) ─────────────────────
  deepSpace: {
    ...base,
    id: "deepSpace", name: "Deep Space", dark: true,
    accent:      "#0e9cc0",
    accentSoft:  "#0e9cc020",
    // legacy keys — used by all page components
    bg:          "#020b14",
    panel:       "#060e1c",
    border:      "#0c1f38",
    text:        "#c5d8e8",
    muted:       "#3f5e7a",
    bright:      "#e4eff8",
    eqBg:        "#020810",
    eqBorder:    "#0c1f38",
    cardBg:      "#060e1c",
  },

  // ── 2. Obsidian (dark, deep purple-black — Catppuccin-inspired) ─────────────
  obsidian: {
    ...base,
    id: "obsidian", name: "Obsidian", dark: true,
    accent:      "#cba6f7",
    accentSoft:  "#cba6f720",
    bg:          "#0d0d14",
    panel:       "#13131e",
    border:      "#1f1f32",
    text:        "#cdd6f4",
    muted:       "#585b70",
    bright:      "#e6e8f8",
    eqBg:        "#09090f",
    eqBorder:    "#1f1f32",
    cardBg:      "#13131e",
  },

  // ── 3. Midnight (dark, GitHub-style professional navy) ──────────────────────
  midnight: {
    ...base,
    id: "midnight", name: "Midnight", dark: true,
    accent:      "#58a6ff",
    accentSoft:  "#58a6ff20",
    bg:          "#0d1117",
    panel:       "#161b22",
    border:      "#21262d",
    text:        "#c9d1d9",
    muted:       "#6e7681",
    bright:      "#f0f6fc",
    eqBg:        "#0d1117",
    eqBorder:    "#30363d",
    cardBg:      "#161b22",
  },

  // ── 4. Carbon (dark, IBM Carbon-inspired warm charcoal) ─────────────────────
  carbon: {
    ...base,
    id: "carbon", name: "Carbon", dark: true,
    accent:      "#78a9ff",
    accentSoft:  "#78a9ff20",
    bg:          "#161616",
    panel:       "#1c1c1c",
    border:      "#393939",
    text:        "#f4f4f4",
    muted:       "#8d8d8d",
    bright:      "#ffffff",
    eqBg:        "#131313",
    eqBorder:    "#393939",
    cardBg:      "#1c1c1c",
  },

  // ── 5. Slate Pro (light, clean professional) ────────────────────────────────
  slatePro: {
    ...base,
    id: "slatePro", name: "Slate Pro", dark: false,
    accent:      "#0066cc",
    accentSoft:  "#0066cc15",
    bg:          "#f0f4f8",
    panel:       "#ffffff",
    border:      "#d4dfe9",
    text:        "#1a2636",
    muted:       "#7a90a8",
    bright:      "#0a1628",
    eqBg:        "#e6eef6",
    eqBorder:    "#c8d8e8",
    cardBg:      "#ffffff",
  },

  // ── 6. Social Pro (LinkedIn-inspired — clean, professional, high readability)
  linkedin: {
    ...base,
    id: "linkedin", name: "Social Pro", dark: false,
    accent:      "#0A66C2",
    accentSoft:  "#0A66C218",
    bg:          "#F3F2EF",
    panel:       "#FFFFFF",
    border:      "#E0E0E0",
    text:        "#434649",
    muted:       "#666666",
    bright:      "#000000",
    eqBg:        "#EEF3F8",
    eqBorder:    "#C0C0C0",
    cardBg:      "#FFFFFF",
  },

  // ── 7. Dark — SMC Design System (navy, cyan accent) ────────────────────────
  dark: {
    ...base,
    id: "dark", name: "Dark", label: "Deep Space", dark: true,
    accent:      "#0e9cc0",
    accentSoft:  "rgba(14,156,192,0.10)",
    bg:          "#0a0f1e",
    panel:       "#0d1526",
    border:      "rgba(255,255,255,0.07)",
    text:        "#e8edf5",
    muted:       "#8892a4",
    bright:      "#f0f6ff",
    eqBg:        "#060b18",
    eqBorder:    "rgba(255,255,255,0.07)",
    cardBg:      "#0d1526",
  },

  // ── 8. Light — SMC Design System (white, sky accent) ───────────────────────
  light: {
    ...base,
    id: "light", name: "Light", label: "Solar", dark: false,
    accent:      "#0284c7",
    accentSoft:  "rgba(2,132,199,0.08)",
    bg:          "#f0f2f6",
    panel:       "#ffffff",
    border:      "rgba(0,0,0,0.09)",
    text:        "#0f172a",
    muted:       "#475569",
    bright:      "#0a0f1e",
    eqBg:        "#e8eaef",
    eqBorder:    "rgba(0,0,0,0.09)",
    cardBg:      "#ffffff",
  },

  // ── 9. Space — SMC Design System (near-black, indigo accent) ───────────────
  space: {
    ...base,
    id: "space", name: "Space", label: "Interstellar", dark: true,
    accent:      "#818cf8",
    accentSoft:  "rgba(129,140,248,0.10)",
    bg:          "#05070f",
    panel:       "#0c0f1c",
    border:      "rgba(129,140,248,0.10)",
    text:        "#f1f5ff",
    muted:       "#8b92b8",
    bright:      "#f1f5ff",
    eqBg:        "#020408",
    eqBorder:    "rgba(129,140,248,0.10)",
    cardBg:      "#0c0f1c",
  },

};

// ── Exports ───────────────────────────────────────────────────────────────────
export const ALL_THEMES = Object.values(THEMES);
export const DEFAULT_THEME_ID = "linkedin";
export const getTheme = (id) => THEMES[id] || THEMES[DEFAULT_THEME_ID];

// Backward-compat aliases used by appContext.js
export const DARK  = THEMES.deepSpace;
export const LIGHT = THEMES.slatePro;
